import { useEffect, useMemo, useReducer, useState } from "react"
import { TreeReloaded } from "event-definitions"
import { RetrievedTree } from "routes/schedule/my-schedules/my-custom-schedules/lib/tree-events"
import { findInChildren } from "library/tree/index.mjs"
import { PlanRoot } from "library/tree/constants.mjs"
import { searchRegimeAssets } from "routes/regime/plugins/regime-search/controller/search-regime-assets"
import { cleanLabel } from "routes/regime/plugins/regime-search/lib/search-labels.mjs"

const MAX_TREE_MATCHES = 30

export function useRegimeSearch(regimes, term) {
    const query = `${term ?? ""}`.trim().toLowerCase()
    const [version, bump] = useReducer(increment, 0)
    const [assetMatches, setAssetMatches] = useState([])
    const [loadingAssets, setLoadingAssets] = useState(false)

    TreeReloaded.useEvent(bump, [])
    RetrievedTree.useEvent(bump, [])

    const regimeIdsKey = (regimes ?? []).map(regimeId).join("|")
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(loadAssets, [query, regimeIdsKey])

    const treeMatches = useMemo(findTreeMatches, [query, regimeIdsKey, version])
    const searchResults = useMemo(combine, [treeMatches, assetMatches])

    return { searchResults, loading: loadingAssets }

    function findTreeMatches() {
        if (!query) return []
        return (regimes ?? []).flatMap(matchesInRegime).sort(regimeRootsFirst).slice(0, MAX_TREE_MATCHES)

        function matchesInRegime(regime) {
            return findInChildren(regime, isMatch).slice(0, MAX_TREE_MATCHES)
        }

        function isMatch(node) {
            const label = `${node.label ?? node.data?.name ?? ""}`.toLowerCase()
            if (label.includes(query)) return true
            const code = `${node.code ?? ""}`.toLowerCase()
            return !!code && code.includes(query)
        }
    }

    function loadAssets() {
        if (!query) {
            setAssetMatches([])
            setLoadingAssets(false)
            return undefined
        }
        let cancelled = false
        setLoadingAssets(true)
        fetchAssetMatches()
            .then((matches) => !cancelled && setAssetMatches(matches))
            .finally(() => !cancelled && setLoadingAssets(false))
        return () => {
            cancelled = true
        }

        async function fetchAssetMatches() {
            try {
                const rows =
                    (await searchRegimeAssets({
                        search: query,
                        regimeIds: (regimes ?? []).map(regimeId),
                    })) ?? []
                const regimesById = new Map((regimes ?? []).map((regime) => [regime.id, regime]))
                return groupSameAssets(rows.map((row) => assetToSearchResult(row, regimesById.get(row.regimeId))))
            } catch (e) {
                console.error("Regime search: failed to search assets", e)
                return []
            }
        }
    }

    function combine() {
        return [...treeMatches, ...assetMatches]
    }
}

function assetToSearchResult(row, regime) {
    return {
        isAsset: true,
        id: `asset:${row.id}:${row.scheduleId}`,
        label: row.label,
        tag: row.tag ?? null,
        scheduleId: row.scheduleId,
        scheduleTitle: row.scheduleTitle ?? "",
        regimeId: row.regimeId,
        breadcrumbs: [
            { label: cleanLabel(regime?.label ?? ""), type: "regime", id: regime?.id },
            { label: row.facility, type: "facility", id: row.facilityId },
            { label: row.floor, type: "floor", id: row.floorId },
            { label: row.space, type: "space", id: row.spaceId },
        ].filter((breadcrumb) => breadcrumb.label),
    }
}

// Collapse identical assets (same label, schedule and location) into one row with a count.
function groupSameAssets(assetResults) {
    return Object.values(assetResults.groupBy(assetGroupKey)).map(toGroupedAsset)
}

function assetGroupKey(assetResult) {
    return [
        assetResult.label,
        assetResult.scheduleId,
        ...assetResult.breadcrumbs.map((breadcrumb) => breadcrumb.label),
    ].join("|")
}

function toGroupedAsset(group) {
    return { ...group[0], count: group.length }
}

function regimeId(regime) {
    return regime.id
}

function increment(n) {
    return n + 1
}

function regimeRootsFirst(a, b) {
    return isRegimeRoot(b) - isRegimeRoot(a)
}

function isRegimeRoot(node) {
    return node[PlanRoot] ? 1 : 0
}
